import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NEW_CATEGORY = { name: "", color: "#C967A3" };

export function EventCategoryPicker({ categories, value, onSelect }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEditList = ["ADMIN", "SUB_ADMIN"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [newCategory, setNewCategory] = useState(NEW_CATEGORY);
  const [savingId, setSavingId] = useState("");
  const selected = categories.find((category) => category.id === value);

  const openManager = () => {
    setDrafts(Object.fromEntries(categories.map((category) => [category.id, { ...category }])));
    setOpen(false);
    setManagerOpen(true);
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["event-categories"] });

  const createCategory = async () => {
    if (!newCategory.name.trim()) return toast.error("Indique o nome da categoria");
    setSavingId("new");
    try {
      const { data } = await api.post("/events/categories", newCategory);
      await refresh();
      setNewCategory(NEW_CATEGORY);
      onSelect(data);
      toast.success("Categoria criada");
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const updateCategory = async (categoryId) => {
    const draft = drafts[categoryId];
    if (!draft?.name.trim()) return toast.error("Indique o nome da categoria");
    setSavingId(categoryId);
    try {
      const { data } = await api.put(`/events/categories/${categoryId}`, {
        name: draft.name,
        color: draft.color,
      });
      await refresh();
      if (value === categoryId) onSelect(data);
      toast.success("Categoria atualizada");
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("Eliminar esta categoria da lista da equipa?")) return;
    setSavingId(categoryId);
    try {
      await api.delete(`/events/categories/${categoryId}`);
      await refresh();
      if (value === categoryId) onSelect(null);
      toast.success("Categoria eliminada");
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSavingId("");
    }
  };

  const changeDraft = (categoryId, field, nextValue) => {
    setDrafts((current) => ({
      ...current,
      [categoryId]: { ...current[categoryId], [field]: nextValue },
    }));
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="mt-1.5 h-11 w-full justify-between px-3 font-normal"
            data-testid="event-category"
          >
            <span className="flex min-w-0 items-center gap-2">
              {selected && <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: selected.color }} />}
              <span className={selected ? "truncate" : "truncate text-muted-foreground"}>
                {selected?.name || "Selecionar categoria"}
              </span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[360px] p-0">
          <Command>
            <CommandInput placeholder="Pesquisar categoria" />
            <CommandList>
              <CommandEmpty>Nenhuma categoria encontrada.</CommandEmpty>
              <CommandGroup>
                {categories.map((category) => (
                  <CommandItem
                    key={category.id}
                    value={category.name}
                    onSelect={() => {
                      onSelect(category);
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: category.color }} />
                    <span className="flex-1">{category.name}</span>
                    {value === category.id && <Check className="h-4 w-4 text-primary" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {canEditList && (
              <>
                <CommandSeparator />
                <button
                  type="button"
                  onClick={openManager}
                  className="flex w-full items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-primary hover:bg-muted"
                >
                  <Pencil className="h-4 w-4" /> Editar lista
                </button>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-poppins">Categorias da agenda</DialogTitle>
            <DialogDescription>
              Esta lista é partilhada por toda a equipa. A cor escolhida aparece nos cartões da agenda.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
            {categories.map((category) => {
              const draft = drafts[category.id] || category;
              return (
                <div key={category.id} className="flex items-center gap-2 rounded-xl border border-border p-2">
                  <input
                    type="color"
                    value={draft.color}
                    onChange={(event) => changeDraft(category.id, "color", event.target.value)}
                    className="h-9 w-10 cursor-pointer rounded border-0 bg-transparent p-0.5"
                    aria-label={`Cor de ${category.name}`}
                  />
                  <Input
                    value={draft.name}
                    onChange={(event) => changeDraft(category.id, "name", event.target.value)}
                    className="h-9 flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => updateCategory(category.id)}
                    disabled={Boolean(savingId)}
                    aria-label="Guardar categoria"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteCategory(category.id)}
                    disabled={Boolean(savingId)}
                    className="text-destructive hover:text-destructive"
                    aria-label="Eliminar categoria"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="border-t border-border pt-4">
            <div className="mb-2 text-sm font-medium">Nova categoria</div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={newCategory.color}
                onChange={(event) => setNewCategory((current) => ({ ...current, color: event.target.value }))}
                className="h-10 w-10 cursor-pointer rounded border-0 bg-transparent p-0.5"
                aria-label="Cor da nova categoria"
              />
              <Input
                value={newCategory.name}
                onChange={(event) => setNewCategory((current) => ({ ...current, name: event.target.value }))}
                onKeyDown={(event) => event.key === "Enter" && createCategory()}
                placeholder="Nome da categoria"
                className="h-10 flex-1"
              />
              <Button type="button" onClick={createCategory} disabled={Boolean(savingId)}>
                <Plus className="mr-1.5 h-4 w-4" /> Criar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
